import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { adminViews, isForbidden } from '@cwos/api-client';
import type { LocationVerification, Severity } from '@cwos/ui-shared';
import {
  DualTimestamp,
  NeedRow,
  Pill,
  SkeletonSet,
  VerificationBadge,
  type NeedSeverity,
} from '@cwos/ui-admin';
import { useNav } from '../navigation.js';
import { ADMIN_VIEWS_READ, usePermissions } from '../permissions.js';
import { coveragePct, livePunches, sessionSeverity, varianceLabel } from './sessionMetrics.js';
import { ShiftPolicyBinding } from './ShiftPolicyBinding.js';
import { formatShiftWindow } from '../../lib/timeFormat.js';
import { LoadFailure } from '../LoadFailure.js';

const SEV_TO_NEED: Record<string, NeedSeverity> = {
  critical: 'urgent',
  warning: 'look',
  info: 'unsure',
};
const SEV_TO_PILL: Record<string, Severity> = {
  urgent: 'urgent',
  look: 'look',
  good: 'good',
};

/**
 * Session detail (doc 01 A2.3/A2.5). The one session told as facts: punches
 * (DualTimestamp + VerificationBadge only), scheduled-vs-actual, breadcrumb
 * coverage, open exceptions, and adjustment history. No punch time or
 * verification is ever rendered outside its dedicated component.
 */
export function SessionDetail() {
  const navigate = useNav();
  const params = useParams({ strict: false }) as { sessionId?: string };
  const id = params.sessionId ?? '';

  /**
   * `GET /api/admin-views/sessions/:id` is guarded by `admin-views:read`
   * (admin-views/routes.ts:142), and `/sessions/$sessionId` is an UNGATED route
   * — everything that links to a session sends the reader straight here. The
   * read asked only whether the URL carried an id, so a principal without the
   * key (`worker` holds none by design, 1755220000000) fired a request that
   * could only 403 and was answered with "This session isn't one of yours" — a
   * sentence about SCOPE, which promises they can open their own and their
   * team's. They cannot open any.
   */
  const { has, isLoading: permissionsLoading, isError: permissionsError } = usePermissions();
  const mayReadSessions = has(ADMIN_VIEWS_READ);

  const query = useQuery({
    queryKey: ['admin-views', 'session', id],
    queryFn: () => adminViews.session(id),
    enabled: id !== '' && mayReadSessions,
  });

  if (permissionsLoading) return <SkeletonSet surface="drawer" />;
  // Unknown is not denied: a permission set that could not be READ falls
  // through to the request, which answers for itself.
  if (!permissionsError && !mayReadSessions) return <SessionsNotYours />;
  if (query.isLoading) return <SkeletonSet surface="drawer" />;
  /*
    A refusal is not a failure, and must not be offered a Retry button.

    Session detail is scoped to the reader's own worker row plus their direct
    reports, so a supervisor who follows a link to a colleague's session now
    gets a 403. Falling through to "We couldn't load this just now" would call a
    settled answer a transient one and invite an endless retry loop; it would
    also be untrue, which matters more.
  */
  if (query.isError && isForbidden(query.error)) return <NotYourSession />;
  if (query.isError && !query.data) {
    return (
      <LoadFailure
        error={query.error}
        message="We couldn’t load this session just now."
        onRetry={() => void query.refetch()}
        mainRead
      />
    );
  }
  const detail = query.data;
  if (!detail) return null;

  const sev = sessionSeverity(detail);
  const punches = livePunches(detail);
  const cmp = detail.scheduledComparison;
  const pct = coveragePct(detail.breadcrumbCoverageRatio);
  const openExceptions = detail.exceptions.filter((e) => e.status !== 'resolved');

  return (
    <div className="rn-col" style={{ maxWidth: 760 }}>
      <div>
        <button type="button" className="rn-link" onClick={() => navigate('/people')}>
          ← Team
        </button>
      </div>

      <section className="rn-card">
        <div className="rn-card-head">
          <h2 className="rn-card-title">{detail.workerDisplayName}</h2>
          <Pill variant={SEV_TO_PILL[sev] ?? 'plain'} dot>
            {detail.status}
          </Pill>
        </div>
        <p className="rn-muted" style={{ marginTop: 0 }}>
          {detail.autoClosed ? 'Auto-closed, keeping the real times' : 'Session record'}
        </p>
      </section>

      {/* Scheduled vs actual */}
      {cmp ? (
        <section className="rn-card">
          <h2 className="rn-card-title">Scheduled vs actual</h2>
          <p style={{ marginBottom: 0 }}>
            Scheduled{' '}
            <strong>
              {formatShiftWindow(cmp.shiftStartsAt, cmp.shiftEndsAt, cmp.shiftTimezone)}
            </strong>{' '}
            ({cmp.scheduledMinutes} min) ·{' '}
            {cmp.actualMinutes !== null ? (
              <>
                actual <strong>{cmp.actualMinutes} min</strong> — {varianceLabel(cmp)}
              </>
            ) : (
              varianceLabel(cmp)
            )}
          </p>
        </section>
      ) : (
        <section className="rn-card">
          <h2 className="rn-card-title">Scheduled vs actual</h2>
          <p className="rn-muted" style={{ marginBottom: 0 }}>
            No shift was scheduled — this was an unscheduled session.
          </p>
        </section>
      )}

      {/* Punches */}
      <section className="rn-card">
        <h2 className="rn-card-title">On the clock</h2>
        {punches.length === 0 ? (
          <p className="rn-muted" style={{ marginBottom: 0 }}>
            No punches recorded yet.
          </p>
        ) : (
          <ul
            style={{
              listStyle: 'none',
              margin: 'var(--space-10) 0 0',
              padding: 0,
              display: 'grid',
              gap: 'var(--space-10)',
            }}
          >
            {punches.map((p) => (
              <li
                key={p.id}
                style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-12)' }}
              >
                <span style={{ minWidth: 84, fontWeight: 600 }}>{p.kind}</span>
                <DualTimestamp occurredAt={p.occurredAt} receivedAt={p.receivedAt} />
                <span style={{ marginLeft: 'auto' }}>
                  <VerificationBadge
                    verification={p.locationVerification as LocationVerification}
                  />
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Breadcrumb coverage */}
      {detail.breadcrumbExpectedCount !== null ? (
        <section className="rn-card">
          <h2 className="rn-card-title">Location coverage</h2>
          <p style={{ marginBottom: 0 }}>
            {/* `?? 0` here read "0 of 12 expected points" — an affirmative
                claim that no location points arrived — out of a field that said
                it did not know. `breadcrumbReceivedCount` is nullable
                independently of `breadcrumbExpectedCount`, and the guard above
                only tests the second. The percentage one line down already
                handles its own null correctly; the numerator did not. */}
            <strong>
              {detail.breadcrumbReceivedCount === null
                ? `Not known, of ${detail.breadcrumbExpectedCount}`
                : `${detail.breadcrumbReceivedCount} of ${detail.breadcrumbExpectedCount}`}
            </strong>{' '}
            expected points{pct !== null ? ` (${pct}%)` : ''}
            {detail.breadcrumbCoverageStatus ? ` · ${detail.breadcrumbCoverageStatus}` : ''}
          </p>
        </section>
      ) : null}

      {/*
        WHAT THIS SHIFT WAS BOUND TO — its own read, its own permission key and
        its own four states, deliberately not folded into the session payload
        above. `GET /api/admin-views/sessions/:id` is `admin-views:read`; the
        binding is `time:read` plus own-worker scoping, and the two are granted
        to different sets of people. A reader who holds one and not the other
        gets the session and a stated refusal for the panel, rather than a 403
        that takes the whole screen down.
      */}
      <ShiftPolicyBinding sessionId={id} />

      {/* Open exceptions */}
      {openExceptions.length > 0 ? (
        <section className="rn-card">
          <h2 className="rn-card-title">Worth a look</h2>
          <div style={{ marginTop: 'var(--space-10)' }}>
            {openExceptions.map((e) => (
              <NeedRow
                key={e.id}
                severity={SEV_TO_NEED[e.severity] ?? 'unsure'}
                icon={SEV_TO_NEED[e.severity] ?? 'unsure'}
                title={e.kind}
                message={e.status}
              />
            ))}
          </div>
        </section>
      ) : null}

      {/* Adjustments */}
      {detail.adjustments.length > 0 ? (
        <section className="rn-card">
          <h2 className="rn-card-title">Time fixes</h2>
          <ul style={{ margin: 'var(--space-10) 0 0', paddingLeft: 'var(--space-16)' }}>
            {detail.adjustments.map((a) => (
              <li key={a.id}>
                {a.requestType} — {a.status}
                {a.reason ? `: ${a.reason}` : ''}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

/**
 * Said plainly, and said as a boundary rather than as an error.
 *
 * Naming what would be needed is deliberate: a reader refused here should know
 * whether to ask for the session or ask for the permission, and a screen that
 * only says "no" leaves them guessing which.
 */
/**
 * No key at all, as distinct from the scope message below it.
 *
 * `NotYourSession` tells the reader they can open their own sessions and their
 * team's, which is exactly what a principal without `admin-views:read` cannot
 * do — it would send them hunting for a link that does not exist for them.
 */
function SessionsNotYours() {
  const navigate = useNav();
  return (
    <section className="rn-card" role="status">
      <h2 className="rn-card-title">Session records aren’t shown to you</h2>
      <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
        Opening a recorded shift — its punches, how each one was verified, and any correction made
        to it — is the administrative view of somebody’s time, and it is a separate permission from
        recording your own. Ask your org admin if you need it.
      </p>
      <button
        type="button"
        className="rn-link"
        style={{ marginTop: 'var(--space-8)' }}
        onClick={() => navigate('/people')}
      >
        ← Back to your team
      </button>
    </section>
  );
}

function NotYourSession() {
  const navigate = useNav();
  return (
    <section className="rn-card">
      <h2 className="rn-card-title">This session isn’t one of yours</h2>
      <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
        You can open your own sessions and those of the workers assigned to you. Reading anybody
        else’s shift — their punches, and the reasons behind any correction to their time — needs an
        organization-wide permission.
      </p>
      <button
        type="button"
        className="rn-link"
        style={{ marginTop: 'var(--space-8)' }}
        onClick={() => navigate('/people')}
      >
        ← Back to your team
      </button>
    </section>
  );
}
